import {
  FareInfographicsMosaic,
  FareCardSkeleton,
  InclusionContent,
  SectionContainer,
  SectionTitleBody,
  BackgroundColor,
  BlockContentValue,
  UnbundlingFareInfographicsMosaic,
  UnbundlingFareInfographicSkeleton
} from "@silversea-ssc/ui-components-react";
import { CruiseGroupCode } from "@silversea-ssc/web-cruise-groups";
import PageBuilderSectionTitle from "components/PageBuilder/PageBuilderSectionTitle";
import React from "react";
import { CruisePriceAndOffersV2Response } from "src/ssc-api/prices/useCruisePrice";
import { ECommerceStatus } from "src/ssc-api/useECommerceStatus";
import {
  CancellationPolicyModalContentFragment,
  CruiseFareInfographicContentFragment
} from "types/graphql-types";
import { fareByPriority, fareContentByFareCodes } from "features/fare/fares";
import * as styles from "./CruiseFares.module.scss";
import { arrayIsNotEmpty, isPresent } from "src/utils/checks";
import CruiseFareCard, { CruiseFareCardProps } from "./CruiseFareCard";
import { InclusionsDrawerProvider } from "features/inclusions/InclusionsDrawer/InclusionsDrawerProvider";
import { FarePenaltiesModalProvider } from "src/features/cancellation-policy/FarePenaltiesModal/FarePenaltiesModalProvider";
import {
  ActivePromoForCruise,
  activePromosPerFare,
  activePromosToPromoBadges
} from "src/utils/promos";
import { ExpandableFareCardProvider } from "../ExpandableFareCardProvider";
import {
  reduceToPaymentTermsApiResponse,
  useRemotePaymentTermsPerCruise
} from "features/cancellation-policy/useRemotePaymentTerms";
import { dequal } from "dequal";
import PricesValidUntilChip from "src/page-components/cruise/PricesValidUntilChip";
import { useClientSideFeatureFlag } from "src/utils/feature-flags";
import UnbundlingCruiseFareCard from "./UnbundlingCruiseFareCard";
import { ShorexSelectionDrawerProvider } from "features/shorex/ShorexSelectionDrawerProvider";
import { ActualShorexSelectionDrawerProps } from "features/shorex/ShorexSelectionDrawer/ActualShorexSelectionDrawer";
import { CruiseShorexCredit } from "features/shorex/shorex-credit-from-tokens";
import { showOnlyUnbundlingFare } from "features/fare/unbundling-fares";

type Props = {
  inclusionsContent: InclusionContent[];
  cancellationPolicyModalContent: CancellationPolicyModalContentFragment;
  cruisePrices?: CruisePriceAndOffersV2Response;
  faresContent: CruiseFareInfographicContentFragment[];
  cruiseCode: string;
  cruiseGroup?: CruiseGroupCode;
  sectionHeading?: {
    title?: { localized?: string };
    content?: { localized?: BlockContentValue };
  };
  raqUrl?: string;
  farePricesValidUntilDate?: Date;
  farePricesMayIncreaseOnDate?: Date;
  ecommerceStatus: ECommerceStatus;
  backgroundColor: "neutral10" | "white";
  activePromos: ActivePromoForCruise[];
  onPromoBadgeClicked?: CruiseFareCardProps["onPromoBadgeClicked"];
  pricesValidUntilContent: BlockContentValue | undefined;
  cruiseType: ActualShorexSelectionDrawerProps["cruiseType"];
  shorexCredit: CruiseShorexCredit | undefined;
};

const CruiseFares: React.FC<Props> = ({
  inclusionsContent,
  cancellationPolicyModalContent,
  cruisePrices,
  faresContent,
  cruiseCode,
  cruiseGroup,
  sectionHeading: sectionTitle,
  raqUrl,
  farePricesValidUntilDate,
  farePricesMayIncreaseOnDate,
  ecommerceStatus,
  backgroundColor,
  activePromos,
  onPromoBadgeClicked,
  pricesValidUntilContent,
  cruiseType,
  shorexCredit
}) => {
  const faresInfo = cruisePrices?.fares;
  const isUnbundling = useClientSideFeatureFlag("unbundling");

  const faresByPrices = (faresInfo || [])
    .filter(showOnlyUnbundlingFare(isUnbundling))
    .map((c) => c.fare)
    .filter(isPresent);
  const placeholderFaresNumber = 2; // TODO load from sanity after unbundling golive

  const remotePaymentTerms = useRemotePaymentTermsPerCruise(
    faresByPrices,
    cruiseCode,
    true
  );

  return (
    <React.Fragment>
      <SectionContainer
        spacingTop="regular"
        spacingBottom="condensed"
        backgroundColor={backgroundColor}
        dataContentName="section_fares"
      >
        <PageBuilderSectionTitle {...sectionTitle}>
          {farePricesValidUntilDate && (
            <SectionTitleBody>
              <PricesValidUntilChip
                className={styles.validUntil}
                content={pricesValidUntilContent}
                farePricesValidUntilDate={farePricesValidUntilDate}
                farePricesMayIncreaseOnDate={farePricesMayIncreaseOnDate}
                backgroundColor={
                  backgroundColor == "neutral10" ? "white" : "neutral05"
                }
              />
            </SectionTitleBody>
          )}
        </PageBuilderSectionTitle>
      </SectionContainer>
      <div data-content-name="fares" id="fares-tabs">
        <FarePenaltiesModalProvider
          content={cancellationPolicyModalContent}
          showCruiseGroupDisclaimer={false}
          remotePaymentTerms={reduceToPaymentTermsApiResponse(
            remotePaymentTerms
          )}
        >
          <InclusionsDrawerProvider inclusionsContent={inclusionsContent}>
            {isUnbundling ? (
              <ShorexSelectionDrawerProvider
                cruiseCode={cruiseCode}
                cruiseType={cruiseType}
                shorexCredit={shorexCredit}
              >
                <UnbundlingAllFareCards
                  placeholderFaresNumber={placeholderFaresNumber}
                  faresContent={faresContent}
                  activePromos={activePromos}
                  cruisePrices={cruisePrices}
                  ecommerceStatus={ecommerceStatus}
                  raqUrl={raqUrl}
                  cruiseGroup={cruiseGroup}
                  onPromoBadgeClicked={onPromoBadgeClicked}
                  backgroundColor={backgroundColor}
                  shorexCredit={shorexCredit}
                />
              </ShorexSelectionDrawerProvider>
            ) : (
              <ExpandableFareCardProvider>
                <AllFareCards
                  placeholderFaresNumber={placeholderFaresNumber}
                  faresContent={faresContent}
                  activePromos={activePromos}
                  cruisePrices={cruisePrices}
                  ecommerceStatus={ecommerceStatus}
                  raqUrl={raqUrl}
                  cruiseGroup={cruiseGroup}
                  onPromoBadgeClicked={onPromoBadgeClicked}
                  backgroundColor={backgroundColor}
                />
              </ExpandableFareCardProvider>
            )}
          </InclusionsDrawerProvider>
        </FarePenaltiesModalProvider>
      </div>
    </React.Fragment>
  );
};

export default React.memo(CruiseFares, dequal);

type AllFareCardsProps = {
  placeholderFaresNumber: number;
  faresContent: CruiseFareInfographicContentFragment[];
  activePromos: ActivePromoForCruise[];
  cruiseGroup?: CruiseGroupCode;
  cruisePrices: CruisePriceAndOffersV2Response | undefined;
  raqUrl: string | undefined;
  ecommerceStatus: ECommerceStatus;
  onPromoBadgeClicked: CruiseFareCardProps["onPromoBadgeClicked"] | undefined;
  backgroundColor: BackgroundColor | undefined;
};
const _AllFareCards: React.FC<AllFareCardsProps> = ({
  placeholderFaresNumber,
  faresContent,
  activePromos,
  cruisePrices,
  ecommerceStatus,
  raqUrl,
  cruiseGroup,
  onPromoBadgeClicked,
  backgroundColor
}) => {
  const faresInfo = cruisePrices?.fares;
  const orderedFaresFromPrices = (faresInfo || [])
    .map((c) => c.fare)
    .filter(isPresent)
    .sort(fareByPriority);

  return (
    <FareInfographicsMosaic
      topBackgroundColor={backgroundColor}
      bottomBackgroundColor={backgroundColor}
      spacingBottom="regular"
      oneCardHorizontalLayout
    >
      {!arrayIsNotEmpty(faresInfo) || !isPresent(cruisePrices)
        ? Array.from({ length: placeholderFaresNumber }, (_v, i) => i).map(
            (i) => (
              <FareCardSkeleton
                expandable
                isExpanded={false}
                variant={
                  placeholderFaresNumber == 1 ? "full-horizontal" : "default"
                }
                key={i}
              />
            )
          )
        : orderedFaresFromPrices.map((fareCode) => {
            const fareInfo = faresInfo.find((a) => a.fare == fareCode);
            if (!fareInfo) return null;

            const fareContent = fareContentByFareCodes(
              faresContent,
              fareInfo.fare
            );
            if (!isPresent(fareContent)) return null;

            const fareActivePromos = activePromosPerFare(
              activePromos,
              fareInfo.fare
            );
            const farePromoBadges = activePromosToPromoBadges(fareActivePromos);

            return (
              <CruiseFareCard
                key={fareInfo.fare}
                fareCode={fareInfo.fare}
                cruiseGroup={cruiseGroup}
                cruisePrices={cruisePrices}
                raqCTAUrl={raqUrl}
                ecommerceStatus={ecommerceStatus}
                activePromos={farePromoBadges}
                onPromoBadgeClicked={onPromoBadgeClicked}
                title={fareContent?.card?.title?.localized}
                headline={fareContent.headline?.localized}
                description={fareContent?.card?.description?.localized}
                variant={faresInfo.length == 1 ? "full-horizontal" : "default"}
              />
            );
          })}
    </FareInfographicsMosaic>
  );
};

const AllFareCards = React.memo(_AllFareCards, dequal);

type UnbundlingAllFareCardsProps = AllFareCardsProps & {
  shorexCredit: CruiseShorexCredit | undefined;
};

const _UnbundlingAllFareCards: React.FC<UnbundlingAllFareCardsProps> = ({
  placeholderFaresNumber,
  faresContent,
  activePromos,
  cruisePrices,
  cruiseGroup,
  onPromoBadgeClicked,
  backgroundColor,
  shorexCredit
}) => {
  const faresInfo = cruisePrices?.fares.filter(showOnlyUnbundlingFare(true));
  const orderedFaresFromPrices = (faresInfo || [])
    .map((c) => c.fare)
    .filter(isPresent);

  return (
    <UnbundlingFareInfographicsMosaic
      topBackgroundColor={backgroundColor}
      bottomBackgroundColor={backgroundColor}
      spacingBottom="regular"
    >
      {!arrayIsNotEmpty(faresInfo) || !isPresent(cruisePrices)
        ? Array.from({ length: placeholderFaresNumber }, (_v, i) => i).map(
            (i) => <UnbundlingFareInfographicSkeleton key={i} />
          )
        : orderedFaresFromPrices.map((fareCode) => {
            const fareInfo = faresInfo.find((a) => a.fare == fareCode);
            if (!fareInfo) return null;

            const fareContent = fareContentByFareCodes(
              faresContent,
              fareInfo.fare
            );
            if (!isPresent(fareContent)) return null;

            const fareActivePromos = activePromosPerFare(
              activePromos,
              fareInfo.fare
            );
            const farePromoBadges = activePromosToPromoBadges(fareActivePromos);

            return (
              <UnbundlingCruiseFareCard
                key={fareInfo.fare}
                fareCode={fareInfo.fare}
                cruiseGroup={cruiseGroup}
                cruisePrices={cruisePrices}
                activePromos={farePromoBadges}
                onPromoBadgeClicked={onPromoBadgeClicked}
                title={fareContent?.card?.title?.localized}
                shorexCredit={shorexCredit}
              />
            );
          })}
    </UnbundlingFareInfographicsMosaic>
  );
};

const UnbundlingAllFareCards = React.memo(_UnbundlingAllFareCards, dequal);
